import RenderManager from "../rendering/render-manager";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElementDynamic from "./base-2d-element-dynamic";
import Transition from "./Transition";

const CopyrightText = {
    'byEmbibe': 'Created By Embibe',
    'publicDomain': 'Public Domain or CC-Zero',
    'commercial': 'Commercially Licensed',
    'fairUse': 'Fair Use',
    'unlicensed': 'UNLICENSED',
};
import screenSizes from "../../editor/constants/screenSizes";
const min = 20;

const s3Url = (p) => p.indexOf("http") === 0 || p.indexOf(Q3.s3Url) === 0 || `/${p}`.indexOf(Q3.s3Url) === 0 ? "" : Q3.s3Url;

class Picture extends Base2DElementDynamic {

    _initDefaultVars() {
        super._initDefaultVars();
        this._wh = new THREE.Vector2(0.4, 0.4);

        this._pictureCover = false;
        this._showCaption = true;
        this._titleText = "";
        this._descriptionText = "";
        this._copyright = 'byEmbibe';
        this._navigateToSlide = null;
        this._webp = Q3.browserCapabilities.supports.WebP;
        this._src = "";
        this._ratio = 1;
        this._borderRadius = 0;
        this._resizedTo = {};
        this._originalSize = { width: 0, height: 0, }
    }

    get pictureCover() { return this._pictureCover; }
    set pictureCover(val) { this._pictureCover = val; }
    get showCaption() { return this._showCaption; }
    set showCaption(val) { this._showCaption = val; }
    get titleText() { return this._titleText; }
    set titleText(val) { this._titleText = val; }
    get descriptionText() { return this._descriptionText; }
    set descriptionText(val) { this._descriptionText = val; }
    get copyright() { return this._copyright; }
    set copyright(val) { this._copyright = val; }
    get navigateToSlide() { return this._navigateToSlide; }
    set navigateToSlide(val) { this._navigateToSlide = val; }
    get webp() { return this._webp && this.extension !== "svg" && this.extension !== "gif"; }
    set webp(val) { this._webp = val; }
    get src() { return this._src; }
    set src(val) { this._src = val; }
    get ratio() { return this._ratio; }
    set ratio(val) { this._ratio = val; }
    get borderRadius() { return this._borderRadius; }
    set borderRadius(val) { this._borderRadius = val; }
    get type2d() { return "Picture"; }
    get x1() {return this.x}
    get x2() {return this.x + this.width}
    get y1() {return this.y}
    get y2() {return this.y + this.height}
    get highLightElement() {
        return this.pictureElement
    }
    get points() {
        return [
            {x: this.x1, y: this.y1},
            {x: this.x2, y: this.y1},
            {x: this.x2, y: this.y2},
            {x: this.x1, y: this.y2}
        ]
    }

    _createContainer() {
        const container = super._createContainer();

        container.classList.add('iframe-wrap', 'column', 'media-preloader');
        container.style.position = "absolute";

        //style
        if (Q3.editMode) {
            container.classList.add(Q3.editMode ? "q3-picture-editor" : "q3-picture-viewer");
        }

        container.style.position = "absolute";
        return container;
    }

    _initValues(options) {
        this._pictureCover = "pictureCover" in options ? Boolean(options.pictureCover) : this._pictureCover;

        //interactivity
        this._showCaption = "showCaption" in options ? options.showCaption : this._showCaption;
        this._titleText = "captionText" in options ? options.captionText : "";
        this._descriptionText = "descriptionText" in options ? options.descriptionText : this._descriptionText;
        this._copyright = "copyright" in options ? options.copyright : this._copyright;
        this._navigateToSlide = "action" in options && options.action.type === 'navigate'
            ? options.action.value
            : this._navigateToSlide;

        //dom insertion
        this._originalSize = "originalSize" in options ? options.originalSize : this._originalSize;
        this._resizedTo = "resizedTo" in options ? options.resizedTo : this._resizedTo;

        this._webp = "webp" in options ? options.webp && Q3.browserCapabilities.supports.WebP : this._webp;
        this._src = "src" in options ? options.new ? options.src : Q3.s3Url + this.getSizeSuffix(options.src) : this._src;

        this._ratio = "ratio" in options ? options.ratio : this._ratio;

        this._borderRadius = "borderRadius" in options ? options.borderRadius : this._borderRadius;
    }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        parentLayer.domElement.setAttribute("contains-image", "");

        this.extension = options.extension;
        this.pictureElement = document.createElement('img');

        if (this.disableEvents && !Q3.editMode) {
            this.pictureElement.style.pointerEvents = "none";
        }
        // this.pictureElement.setAttribute('src', options.src);
        this.pictureElement.setAttribute('crossorigin', "anonymous");
        this.pictureElement.setAttribute('draggable', false);

        if (!this.ratio && Q3.SlideManager.currentSlide.layers[this.parentLayer.uuid]) {
            this.domElement.style.display = "none";
            let tempImg = new Image();
            tempImg.onload = () => {
                this.ratio = tempImg.height / tempImg.width;
                this.data.ratio = this.ratio;
                this.domElement && this.redraw();
                this.domElement.style.display = "flex";
            };
            tempImg.src = this.src; // this must be done AFTER setting onload
        }

        this.pictureElement.className = 'q3-picture';
        if (options.className) {
            this.pictureElement.classList.add(options.className)
        }
        this.domElement.appendChild(this.pictureElement);

        this.captionElement = document.createElement("div");
        this.captionElement.classList.add('pic-caption');
        if (Q3.editMode) {
            this.titleElement = document.createElement("input");
            this.titleElement.type = "text";
            this.titleElement.placeholder = "Enter picture title...";
            this.titleElement.value = this.titleText;
            this.titleElement.addEventListener("input", (e) => {
                this.setTitleText(e.target.value)
            })
        } else {
            this.titleElement = document.createElement("div");
            this.titleElement.innerText = this.titleText;
        }
        this.titleElement.classList.add('pic-title');
        if (Q3.editMode) {
            this.descriptionElement = document.createElement("input");
            this.descriptionElement.type = "text";
            this.descriptionElement.placeholder = "Enter picture description...";
            this.descriptionElement.value = this.descriptionText;
            this.descriptionElement.addEventListener("input", (e) => {
                this.setDescriptionText(e.target.value)
            })
        } else {
            this.descriptionElement = document.createElement("div");
            this.descriptionElement.innerText = this.descriptionText;
        }
        this.descriptionElement.classList.add('pic-description');
        this.captionElement.style.display = this.showCaption ? "block" : "none";
        this.captionElement.appendChild(this.titleElement);
        this.captionElement.appendChild(this.descriptionElement);
        this.domElement.appendChild(this.captionElement);

        this.copyrightElement = document.createElement("div");
        this.copyrightElement.classList.add('pic-copyright');
        if (Q3.editMode) {
            this.copyrightElement.classList.add('in-editor');
        }
        this.domElement.appendChild(this.copyrightElement);

        this.updateCaptionVisibility();
        this.updateCopyright();

        if (Q3.isReady) {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        } else {
            Q3.Events.listen("tourReady", () => {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            })
        }

        this.pictureElement.addEventListener('click', e => {
            this.selectHandler(e);
        }, false);

        const action = this.data.action;
        if (action && action.length && !Q3.editMode) {
            this.pictureElement.classList.add("clickable");
            action.forEach(currAction => {
                const transition = new Transition(currAction.systemAction, currAction.value, this);
                if (currAction.userAction === "click") {
                    this.domElement.addEventListener('click', (e) => {
                        transition.click();
                    }, false);
                }
            });
        }

        const { width, height } = RenderManager.getSize();

        this.redraw = this.redraw.bind(this);

        this.handleListeners(true);

        this.redraw(width, height);
        this.toggleCaption(this.showCaption);
        if (Q3.editMode) {
            this.clonesDiv = document.createElement("div");
            this.clonesDiv.className = "clones-container"
            this.domElement.appendChild(this.clonesDiv);
        }
    }

    getSizeSuffix(src) {
        if (Q3.editMode || this.extension === "svg") return src;
        let suffix = "";
        let sizeObj = this.sizeLg;
        let box = {
            width: screenSizes[Q3.windowSize].width * sizeObj.width,
            height: screenSizes[Q3.windowSize].height * sizeObj.height,
        };
        box.resizeBy = this.ratio < box.height / box.width ? "width" : "height";
        if (this._originalSize[box.resizeBy] > box[box.resizeBy]) {
            suffix = this._resizedTo[Q3.windowSize] ? "_" + Q3.windowSize : "";
        }
        let split = src.split(".");
        return split[0] + suffix + "." + split.slice(1).join("");
    }

    getSrc(path, {noS3Url} = {}) {
        if (!s3Url(path)) noS3Url = true;
        if (path.indexOf("data:image") === 0) {
            return {
                main: path,
                fallback: path
            }
        }
        path = path.split("?")[0];
        const isAmpUrl = path.match(/amp\//i);
        return {
            main: `${noS3Url ? "" : isAmpUrl ? Q3.s3Url.replace("\/assets", "") : Q3.s3Url}${path}${!isAmpUrl && this.webp ? ".webp" : ""}`,
            fallback: `${noS3Url ? "" : isAmpUrl ? Q3.s3Url.replace("\/assets", "") : Q3.s3Url}${path}`,
        }
    }

    setSrc(path, opts = {}) {
        if (Q3.SlideManager.currentSlide.layers[this.parentLayer.uuid]) {
            if (path.startsWith("data:")) {
                this.pictureElement.setAttribute('src', path);
            } else {
                const src = this.getSrc(path, opts);
                if (path.split("?")[0].split(".").pop() === "svg") {
                    this.pictureElement.setAttribute('src', src.main);
                } else {
                    this.src = src.main;
                    this.pictureElement.setAttribute('src', src.main);
                    const suffixRegex = new RegExp(/_(fhd|qhd|uhd)\./g);
                    const fallbacks = [];
                    const unmodifiedUrl = `${s3Url(this.data.src)}${this.data.src}`;
                    if (src.main !== unmodifiedUrl) {
                        fallbacks.push(unmodifiedUrl)
                    }
                    if (src.main.match(suffixRegex)) {
                        fallbacks.push(src.main.replace(suffixRegex, "."))
                        if (src.main.endsWith(".webp")) {
                            fallbacks.push(src.main.replace(".webp", ""))
                        }
                    }
                    if (fallbacks.length) {
                        function onError() {
                            console.log(`Picture ERROR: ${this.src}`)
                            this.src = fallbacks.pop();
                            if (!fallbacks.length) {
                                this.removeEventListener("error", onError)
                            }
                        }
                        this.pictureElement.addEventListener("error", onError)
                    }
                }
            }
        }
    }

    handleListeners(add) {
        Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
        Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
    }

    playLeavingAnimation() {
        this.enterExitCtrl.playLeave();
    }

    cancelLeavingAnimation() {
        this.enterExitCtrl.cancelLeave();
    }

    setTitleText(text) {
        this.titleText = text
        this.titleElement.innerText = text;
        this.data.captionText = text;
        this.updateCaptionVisibility();
    }

    setDescriptionText(text) {
        this.descriptionText = text;
        this.descriptionElement.innerText = text;
        this.data.descriptionText = text;
        this.updateCaptionVisibility();
    }

    setCopyright(copyright) {
        this.copyright = copyright;
        this.data.copyright = copyright;
        this.updateCaptionVisibility();
        this.updateCopyright();
    }

    get canShowCaption() {
        return (this.showCaption === undefined || this.showCaption) && (this.descriptionText || this.titleText || Q3.editMode)
    }

    updateCaptionVisibility() {
        this.captionElement.style.display = this.canShowCaption ? "block" : "none";
        this.redraw();
    }

    updateCopyright() {
        this.copyrightElement.innerText = CopyrightText[this.copyright];

        if (this.copyright === 'unlicensed') {
            this.copyrightElement.classList.add('unlicensed');
        } else {
            this.copyrightElement.classList.remove('unlicensed');
        }

        if (this.copyright === 'byEmbibe') {
            this.copyrightElement.classList.add('by-embibe');
        } else {
            this.copyrightElement.classList.remove('by-embibe');
        }

        this.redraw();
    }

    toggleEvents(enabled) {
        this.disableEvents = !enabled;
        this.pictureElement.style.pointerEvents = enabled || Q3.editMode ? "auto" : "none";
    }

    toggleCaption(val) {
        this.showCaption = val;
        this.updateCaptionVisibility();
        this.data.showCaption = val;
        this.redraw();
    }

    setData(data) {

        let currentSize = this.getVWSize();
        Object.assign(this[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });

        this.data[currentSize] = {};

        Object.assign(this.data[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });
        if (currentSize === "sizeSm") {
            this.data.sizeSm.autoResized = true;
        }
        this.redraw();

    }

    calcSize(w, h) {
        h = h - (this.showCaption ? this.captionElement.offsetHeight : 0);
        // if (this.copyright !== 'unlicensed') {
        //     h = h - (this.copyrightElement.offsetHeight-1);
        // }
        if (this.pictureCover) {
            return {
                width: w + "px",
                height: h + "px"
            }
        }
        if (this.ratio === 1) {
            let side = Math.min(w, h);
            return {
                width: side + "px",
                height: side + "px"
            }
        } else if (h > w * this.ratio) {
            return {
                width: w + "px",
                height: w * this.ratio + "px"
            }
        } else {
            return {
                width: h / this.ratio + "px",
                height: h + "px"
            }
        }
    }

    getBBox(w, h) {
        const size = this.getRenderPositionAndSize(w, h);

        return [size.x, size.y, size.x + size.w, size.y + size.h];
    }

    getRenderPositionAndSize(width, height) {
        const pr = RenderManager.pixelRatio;

        if (!width) width = RenderManager.domElement.width / pr;
        if (!height) height = RenderManager.domElement.height / pr;

        let size = this.getVWSize();
        if (this[size]) {
            Object.assign(this, this[size]);
        }

        if (!width) {
            width = size.width;
        }
        if (!height) {
            height = size.height;
        }

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / pr;
            height = Q3.letterbox.offsetHeight / pr;
        }

        let w = this.width * width * pr;
        let h = this.height * height * pr;

        if (w < min) w = min;
        if (h < min) h = min;

        const [x, y] = this._calcPosition(width, height);

        return { x, y, w, h };
    }

    onVisibilityChange(val) {
        if (val && !this.pictureElement.src) {
            this.setSrc(this.src);
        }
    }

    redraw(width, height) {

        if (!width) width = RenderManager.domElement.width / RenderManager.pixelRatio;
        if (!height) height = RenderManager.domElement.height / RenderManager.pixelRatio;

        const { x, y, w, h } = this.getRenderPositionAndSize(width, height);

        Object.assign(this.domElement.style, {
            width: w + 'px',
            height: h + 'px'
        });


        if (Q3.selected === this && this.target && Q3.editMode) {
            Events.fire('viewportchange');
        }

        this.domElement.style.left = (x || 0) + "px";
        this.domElement.style.top = (y || 0) + "px";
        this.domElement.style.position = "absolute";
        if (w >= 0) this.domElement.style.width = w;
        if (h >= 0) this.domElement.style.height = h;

        if (Q3.editMode) {
            if (Q3.mobileView && this.hiddenMobile) {
                this.domElement.style.opacity = 0.4;
            } else if (!Q3.mobileView && this.hiddenDesktop) {
                this.domElement.style.opacity = 0.4;
            } else {
                this.domElement.style.opacity = 1;
            }
        }
        let size = this.calcSize(w, h);
        if (this.pictureCover) {
            this.pictureElement.style.objectFit = "cover";
        } else {
            this.pictureElement.style.removeProperty("objectFit")
        }
        Object.assign(this.pictureElement.style, size);
        let brVal = parseFloat(this.borderRadius) / 15 + "rem";
        let borderRadius = brVal;
        if (this.canShowCaption) {
            this.captionElement.style.width = size.width;
            this.captionElement.style.borderRadius = `0 0 ${brVal} ${brVal}`
            borderRadius = `${brVal} ${brVal} 0 0`;
        }
        this.pictureElement.style.borderRadius = borderRadius;
    }

    delete() {

        this.handleListeners(false);

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        this.parentLayer.domElement.removeChild(this.domElement);

        if (this.targetUuid) {
            for (let i = 0; i < Q3.layers.attachedObjects.length; i++) {
                if (Q3.layers.attachedObjects[i].uuid === this.uuid) {
                    Q3.layers.attachedObjects.splice(i, 1);
                    break;
                }
            }
        }

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }


    clone() {

        const picture = new Picture({
            src: this.src,
            x: this.x + 0.01,
            y: this.y + 0.01,
            targetUuid: this.targetUuid
        },
            this.parentLayer,
            THREE.Math.generateUUID()
        );

        Q3.data.layers.layers2D[picture.parentLayer.uuid].objects[picture.uuid] = {
            type: 'picture',
            src: picture.filename,
            x: picture.x,
            y: picture.y,
            targetUuid: picture.targetUuid
        };

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 0, picture);
        this.parentLayer.objects.forEach((o, i) => o.index = i);

        return picture;

    }

    hide() {

        this.pictureElement.classList.add('q3-picture-occluded');

    }

    show() {

        this.pictureElement.classList.remove('q3-picture-occluded');

    }

}

export default Picture;
export { Picture };
